import React from 'react'
import styled from '@emotion/styled'

import { white, blueBase, blueLight20, grayBase, grayDark40, grayLight20, redLight10, fontSizeXSmall, argoRed, argoBlue, argoDisabled } from '../../constants/principles'
import { fontFamilyOpenSans, fontSize4XSmall, fontSize2XSmall, fontWeightRegular, argoText, argoInputBorder } from '../../constants/principles'
import { useToggler } from '../../hooks'

import ChevronDown from '../../assets/chevron_down.svg'
import PadLock from '../../assets/padlock.svg'

export default ({
  label,
  options = [],
  valueProp = 'displayName',
  textProp = 'displayName',
  backgroundColor,
  onChange,
  getLink,
  currentValue,
  disabled,
  fullWidth = false,
  selectRef,
  showSelect = true,
  selectText = 'Select',
  isError,
  marginRight,
  id,
  isLinksPage,
  testId,
  onClick = () => {},
  useArgoStyle
}) => {
  const [hasFocus, toggleFocus] = useToggler(false)
  const selectedValue = currentValue ? currentValue[valueProp] || currentValue : selectRef ? undefined : ''
  return (
    <StyledDiv fullWidth={fullWidth} useArgoStyle={useArgoStyle}>
      <StyledLabel htmlFor={id} hasFocus={hasFocus} showLabel={hasFocus || label} backgroundColor={backgroundColor} isError={isError} disabled={disabled} useArgoStyle={useArgoStyle} style={{ top: useArgoStyle && (hasFocus || label) && 5 }}>
        {label}
      </StyledLabel>
      <StyledSelect
        name={label}
        data-testid={testId}
        id={id}
        ref={selectRef}
        value={selectedValue}
        onChange={onChange}
        onFocus={toggleFocus}
        onClick={onClick}
        onBlur={toggleFocus}
        disabled={disabled || !options.length}
        fullWidth={fullWidth}
        isError={isError}
        marginRight={marginRight}
        useArgoStyle={useArgoStyle}>
        {showSelect && (
          <option key={''} value={''}>
            {selectText}
          </option>
        )}
        {options.map((option, i) => (
          <option key={i} value={option[valueProp]}>
            {option[textProp]}
          </option>
        ))}
      </StyledSelect>
      {getLink && (
        <StyledOptions isLinksPage={isLinksPage}>
          {options.map((option, i) => {
            const link = getLink(option[valueProp], undefined, isLinksPage, true, i)
            return !link ? null : (
              <a key={i} href={__PATH_PREFIX__ + link}>
                {option[textProp]}
              </a>
            )
          })}
        </StyledOptions>
      )}
      {useArgoStyle && (
        <ArgoSelectIcon disabled={disabled}>
          <svg viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M1 1l4 4 4-4" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </ArgoSelectIcon>
      )}
    </StyledDiv>
  )
}

const ArgoSelectIcon = styled.div`
  align-items: center;
  color: ${p => (p.disabled ? 'silver' : '#006bc2')};
  display: flex;
  height: 100%;
  pointer-events: none;
  position: absolute;
  right: 16px;
  width: 16px;
`

const StyledOptions = styled.div`
  background-color: white;
  display: flex;
  flex-direction: column;
  position: absolute;
  top: 0;
  z-index: ${p => (p.isLinksPage ? 100 : -100)};
  overflow: auto;
  bottom: 0;
  min-width: 60px;
  padding-right: 5px;
  margin-left: 5px;
  margin-top: 5px;
  margin-bottom: 3px;
  line-height: 40px;
`

const StyledDiv = styled.div`
  position: relative;
  font-family: ${fontFamilyOpenSans};
  line-height: 0.1em;
  text-align: left;
  ${p => p.fullWidth && `width: 100%;`}
  ${p =>
    p.useArgoStyle &&
    `
    display: inline-flex;
    flex-basis: 100%;
    `}
`

const StyledLabel = styled.label`
  font-size: ${fontSize4XSmall};
  font-weight: ${fontWeightRegular};
  position: absolute;

  ${p =>
    !p.useArgoStyle
      ? `
    display: inline;
    border-radius: 3px;
    margin: 0px 0px 0px 9px;
    padding: 0 4px;
    vertical-align: top;
    background-color: ${p.backgroundColor || white};
    color: ${p.hasFocus ? blueLight20 : p.isError ? redLight10 : p.disabled ? grayLight20 : grayDark40};
  `
      : `
    color: ${p.hasFocus ? '#006bc2' : p.isError ? argoRed : p.disabled ? 'silver' : argoText};
    cursor: text;
    font-size: ${p.showLabel ? fontSize4XSmall : fontSize2XSmall};
    inset: 16px;
    line-height: ${p.showLabel ? '14px' : '16px'};
    pointer-events: none;
    transform-origin: top left;
    transition-duration: 0.15s;
    transition-property: all;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  `}
`

const StyledSelect = styled.select`
  width: inherit;
  opacity: initial;
  display: block;
  font-size: ${p => (p.useArgoStyle ? fontSizeXSmall : fontSize2XSmall)};
  padding: ${p => (p.useArgoStyle ? '16px 12px 8px 16px' : '0 24px 0 6px')};
  border-radius: 4px;
  border: 1px solid ${p => (p.useArgoStyle ? argoInputBorder : grayBase)};
  border-color: ${p => p.isError && (p.useArgoStyle ? argoRed : redLight10)};
  line-height: ${p => (p.useArgoStyle ? '1.375' : 'normal')};
  -moz-appearance: none;
  -webkit-appearance: none;
  background-color: ${white};

  ${p =>
    !p.useArgoStyle
      ? `
    margin-right: ${p.marginRight || '16px'};
    height: 42px;
    border-color: ${p.disabled && grayLight20};
    background-image: url(${p.disabled ? `${PadLock}` : `${ChevronDown}`});
    background-size: ${p.disabled ? '16px auto, 100%' : '12px auto, 100%'};
    background-position: right 8px top 50%, 0, 0;
    background-repeat: no-repeat, repeat;
    &:focus {
      border-color: ${blueBase}};
      outline: none;
    }
    &::-ms-expand {
      display: none;
    }`
      : `
    appearance: none;
    letter-spacing: 0;
    max-width: 100%;
    outline: 2px solid transparent;
    outline-offset: 2px;
    resize: none;
    transition-duration: 0.2s;
    transition-property: color, background-color, border-color, text-decoration-color, fill, stroke;
    transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
    width: 100%;
    :hover {
      border-color: #939393;
    }
    :focus {
      background-color: ${white};
      border-color: ${argoBlue};
      box-shadow: 0 0 8px 0 ${argoBlue};
      color: ${argoText};
    }
  `};

  :disabled {
    background-color: ${p => p.useArgoStyle && argoDisabled};
    border-color: ${p => p.useArgoStyle && 'silver'};
    color: ${p => (p.useArgoStyle ? 'silver' : grayBase)};
  }
`
